import {makeAutoObservable} from 'mobx';

export enum FolderIconType {
  AllItems = 'all-items',
  Casus = 'casus',
  Folder = 'folder',
  Trash = 'trash',
  Justitia = 'justitia',
  JustitiaSubmission = 'justitia-submission',
}

export enum FolderType {
  AllItems = 'all-items-folder',
  Trash = 'trash-folder',
  Justitia = 'justitia-folder',
}

export type FolderData = {
  id: string;
  name: string;
  hasSubFolders: boolean;
};

export type NestedFolderStructure = {
  folder: FolderData;
  subFolders: FolderData[];
  subFolderInThePathToTheSelectedFolder?: NestedFolderStructure;
};

export type FolderOptions = {
  isExpandable?: boolean;
  isExpanded?: boolean;
  isExpansionChevronDisabled?: boolean;
  isDisabled?: boolean;
  isDropDisabled?: boolean;
  isNotSelectable?: boolean;
  iconType?: FolderIconType;
  folderColor?: FolderColor;
  infoText?: string;
};

export type FolderColor = 'BLACK' | 'ORANGE' | 'DARK_BLUE' | 'GREEN' | 'GREY';

export class Folder {
  public readonly id: string;
  private _name: string;
  public readonly iconType: FolderIconType;
  /** Parent folder of the folder. The current folder needs to be listed as a subfolder of the parent is expanded. */
  public parent?: Folder;

  /**
   * List of all subfolders. This list is empty until the folder is expanded
   * and the subfolders are loaded on demand.
   */
  public subFolders: Folder[] = [];

  /** Whether the folder has subfolders. This should also be set if the subfolders are not yet expanded. */
  public hasSubFolders: boolean;
  private _isExpanded: boolean;
  private _isExpansionChevronDisabled: boolean;
  private _isDisabled: boolean;
  private _isDropDisabled: boolean;
  private _isNotSelectable: boolean;
  private _isExpandable: boolean;

  private _folderColor?: FolderColor;

  public infoText?: string;

  public constructor(
    {id, name, hasSubFolders}: FolderData,
    parent?: Folder,
    options?: FolderOptions
  ) {
    makeAutoObservable(
      this,
      {
        id: false,
        iconType: false,
      },
      {autoBind: true}
    );
    this.id = id;
    this._name = name;
    this.iconType = options?.iconType ?? FolderIconType.Folder;
    this.parent = parent;
    this.hasSubFolders = hasSubFolders;
    this._isExpanded = options?.isExpanded ?? false;
    this._isDisabled = options?.isDisabled ?? false;
    this._isDropDisabled = options?.isDropDisabled ?? false;
    this._isExpansionChevronDisabled =
      options?.isExpansionChevronDisabled ?? false;
    this._isNotSelectable = options?.isNotSelectable ?? false;
    this._folderColor = options?.folderColor;
    this._isExpandable = options?.isExpandable ?? true;
    this.infoText = options?.infoText;
  }

  public get folderColor(): FolderColor | undefined {
    return this._folderColor;
  }

  public get isRoot(): boolean {
    return this.parent === undefined;
  }

  public static createRootCasusFolder(
    folderData: FolderData,
    isExpandable = false
  ): Folder {
    return new Folder(folderData, undefined, {
      isExpandable,
      isExpanded: !isExpandable || folderData.hasSubFolders,
      iconType: FolderIconType.Casus,
    });
  }

  public static createAllItemsFolder(name: string, infoText: string): Folder {
    return new Folder(
      {
        hasSubFolders: false,
        id: FolderType.AllItems,
        name,
      },
      undefined,
      {
        isExpandable: false,
        iconType: FolderIconType.AllItems,
        infoText,
      }
    );
  }

  public static createTrashFolder(name: string, isDisabled?: boolean): Folder {
    return new Folder(
      {
        name,
        hasSubFolders: false,
        id: FolderType.Trash,
      },
      undefined,
      {
        isExpandable: false,
        iconType: FolderIconType.Trash,
        isDisabled,
      }
    );
  }

  public get name(): string {
    return this._name;
  }

  public setInternalName(newName: string): void {
    this._name = newName;
  }

  public setInternalSubFolders(subFolders: Folder[]): void {
    this.subFolders = subFolders.toSorted((folderA, folderB) =>
      folderA.name.localeCompare(folderB.name)
    );
    this.hasSubFolders = subFolders.length > 0;
  }

  public setInternalParent(folder: Folder): void {
    this.parent = folder;
  }

  public appendToInternalSubFolder(folder: Folder): void {
    this.subFolders = [...this.subFolders, folder].toSorted(
      (folderA, folderB) => folderA.name.localeCompare(folderB.name)
    );
    this.hasSubFolders = true;
  }

  public get isExpanded(): boolean {
    return this._isExpanded;
  }

  public set isExpanded(value: boolean) {
    this._isExpanded = value;
  }

  public get isDisabled(): boolean {
    return this._isDisabled;
  }

  public get isDropDisabled(): boolean {
    return this._isDropDisabled;
  }

  public get isExpansionChevronDisabled(): boolean {
    return this._isExpansionChevronDisabled;
  }

  public get isExpandable(): boolean {
    return this._isExpandable;
  }

  public get isNotSelectable(): boolean {
    return this._isNotSelectable;
  }

  /**
   * For each element in folderData create a new folder and set it as a child
   * of the current folder. The order of the subfolders is maintained.
   * @param folderData data of the subfolders
   */
  public setSubFolders(folderData: FolderData[]): void {
    this.subFolders = folderData.map(
      (f) =>
        new Folder(f, this, {
          isDisabled: this._isDisabled,
          iconType: FolderIconType.Folder,
        })
    );
  }

  public addSubFolder(
    folderData: FolderData,
    folderOptions: FolderOptions
  ): void {
    this.subFolders.push(new Folder(folderData, this, folderOptions));
  }

  /**
   * Recursively set all of the subfolders of the expanded folder structure
   * passed and return the deepest folder in the expanded folder tree.
   * It can only follow one folder tree path while setting the subfolders.
   * @param folderStructure the folder structure to set
   * @returns The deepest folder in the expanded folder tree
   */
  public setNestedSubFolders(folderStructure: NestedFolderStructure): Folder {
    this.setSubFolders(folderStructure.subFolders);
    const relevantSubFolderId =
      folderStructure.subFolderInThePathToTheSelectedFolder?.folder.id;
    if (relevantSubFolderId) {
      this._isExpanded = true;
      const relevantSubFolder = this.subFolders.find(
        (folder) => folder.id === relevantSubFolderId
      );
      return relevantSubFolder
        ? relevantSubFolder.setNestedSubFolders(
            folderStructure.subFolderInThePathToTheSelectedFolder!
          )
        : this;
    } else {
      return this;
    }
  }

  /**
   * Get the folder which is visually displayed as the element before this one.
   * If there is a sibling folder earlier in the subfolders order then return that.
   * If there is no such sibling folder return the parent folder if present, else undefined.
   * @returns The sibling folder, the parent folder or undefined
   */
  public getPreviousInTree(): Folder | undefined {
    if (!this.parent) {
      return undefined;
    }

    const currentFolderIndex = this.parent.subFolders.findIndex(
      (folder) => folder.id === this.id
    );

    let previousElement: Folder;
    if (currentFolderIndex !== 0) {
      previousElement =
        this.parent.subFolders[currentFolderIndex - 1].getLastChildElement();
    } else {
      previousElement = this.parent;
    }
    return previousElement.isDisabled
      ? previousElement.getPreviousInTree()
      : previousElement;
  }

  private getLastChildElement(): Folder {
    if (!this.isExpanded || !this.hasSubFolders) {
      return this;
    }
    return this.subFolders[this.subFolders.length - 1].getLastChildElement();
  }

  /**
   * Get the folder which is visually displayed as the element after this one.
   * If the folder is expanded and has subfolders, return the first child folder.
   * If not and there is a sibling folder later in the subfolders order then return that,
   * otherwise undefined.
   * @returns The child folder, the sibling folder or undefined
   */
  public getNextInTree(skipSubFolders = false): Folder | undefined {
    if (!skipSubFolders && this.isExpanded && this.hasSubFolders) {
      return this.subFolders[0].isDisabled
        ? this.subFolders[0].getNextInTree()
        : this.subFolders[0];
    }

    if (!this.parent) {
      return undefined;
    }

    const currentFolderIndex = this.parent.subFolders.findIndex(
      (folder) => folder.id === this.id
    );
    if (currentFolderIndex + 1 < this.parent.subFolders.length) {
      const nextSibling = this.parent.subFolders[currentFolderIndex + 1];
      return nextSibling.isDisabled ? nextSibling.getNextInTree() : nextSibling;
    } else {
      return this.parent.getNextInTree(true);
    }
  }

  /**
   * Find the folder with the given folderId in the folder tree
   * @param folderId The folderId to search for
   * @returns the folder if it exists in the visible tree else undefined
   */
  public find(folderId: string): Folder | undefined {
    if (folderId === this.id) {
      return this;
    }
    const foundFolder = this.subFolders.find(
      (folder) => folder.id === folderId
    );
    if (foundFolder) {
      return foundFolder;
    }

    for (const subFolder of this.subFolders) {
      const foundSubfolder = subFolder.find(folderId);
      if (foundSubfolder) {
        return foundSubfolder;
      }
    }
    return undefined;
  }

  /**
   * Disable the folder including all its subfolders
   */
  public disable(): void {
    this._isDisabled = true;
    this.subFolders.forEach((folder) => folder.disable());
  }

  public disableDropOnSubfolders(): void {
    this.subFolders.forEach((folder) => folder.disableDrop());
  }

  public enableDropOnSubfolders(): void {
    this.subFolders.forEach((folder) => folder.enableDrop());
  }

  public disableDrop(): void {
    this._isDropDisabled = true;
  }

  public enableDrop(): void {
    this._isDropDisabled = false;
  }
}
